import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { PinIcon } from "@xola/icons";
import { Button, flash } from "@xola/ui-kit";
import clsx from "clsx";
import { FormField, TextareaField } from "common";
import { FormikErrors } from "formik/dist/types";
import { isEmpty, isNil } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import Geocode from "react-geocode";
import { Location, NullableCoordinates } from "types";
import { Loader } from "@/components/Loader";
import { config } from "@/config";

const containerStyle = {
    width: "560px",
    height: "240px",
    borderRadius: "7px",
};

interface LocationFieldProps {
    geo?: NullableCoordinates;
    isRequired?: boolean;
    address: string;
    handleAddressChange: (address: string) => any;
    handleLocationChange: (geo: google.maps.LatLngLiteral) => any;
    errors?: FormikErrors<Location | string>;
    enableMap: boolean;
    setEnableMap: (value: boolean) => void;
}

interface MapBounds {
    northeast: google.maps.LatLngLiteral;
    southwest: google.maps.LatLngLiteral;
}

export const LocationField = ({
    geo,
    address,
    isRequired = true,
    handleAddressChange,
    handleLocationChange,
    errors = {},
    enableMap = false,
    setEnableMap,
}: LocationFieldProps) => {
    Geocode.setApiKey(config.google.apiKey);

    const [localGeo, setLocalGeo] = useState(isEmpty(geo) ? { lat: 0, lng: 0 } : (geo as google.maps.LatLngLiteral));
    const hasValidGeoCoordinates = !isNil(localGeo?.lat) && !isNil(localGeo.lng);

    const [center, setCenter] = useState(() => {
        return hasValidGeoCoordinates ? localGeo : { lat: 0, lng: 0 };
    });

    const [zoom, setZoom] = useState(() => {
        return hasValidGeoCoordinates ? 10 : 1;
    });

    const [markerLocation, setMarkerLocation] = useState(() => {
        if (hasValidGeoCoordinates) {
            const coordinates: google.maps.LatLngLiteral = {
                lat: localGeo?.lat ?? 0,
                lng: localGeo?.lng ?? 0,
            };

            return coordinates;
        }
    });

    const handleMapClick = (...events: google.maps.MapMouseEvent[]) => {
        const event = events[0];
        const latlng = event.latLng;

        if (latlng?.lat() && latlng.lng()) {
            const updatedGeo = {
                lat: latlng.lat(),
                lng: latlng.lng(),
            };

            setLocalGeo(updatedGeo);
            setMarkerLocation(updatedGeo);
            handleLocationChange(updatedGeo);
        }
    };

    const handleAddressResolve = (geo: google.maps.LatLngLiteral, bounds: MapBounds, types: string[]) => {
        setCenter(geo);
        setZoom(getZoom(bounds.southwest.lng, bounds.northeast.lng));
        if (types.includes("street_address")) {
            setMarkerLocation(geo);
            handleLocationChange(geo);
        }
    };

    const handleAddressLookup = async (address: string) => {
        setEnableMap(true);
        if (address) {
            try {
                const { results } = await Geocode.fromAddress(address);
                handleAddressResolve(results[0].geometry.location, results[0].geometry.viewport, results[0].types);
            } catch {
                flash.danger("Unable to find a location matching that address");
            }
        }
    };

    const shouldUpdateLocation = useMemo(() => {
        return enableMap && isEmpty(geo) && localGeo;
    }, [enableMap, geo, localGeo]);

    useEffect(() => {
        if (shouldUpdateLocation) {
            handleLocationChange(localGeo);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldUpdateLocation]);

    return (
        <div className="space-y-3">
            <div className="flex space-x-2">
                <FormField name="pickupAddress" className="w-full">
                    <TextareaField
                        isRequired={isRequired}
                        value={address}
                        rows={3}
                        className="h-10"
                        onChange={(e: any) => handleAddressChange(e.target.value)}
                    />
                </FormField>
                <Button
                    variant="outline"
                    color="secondary"
                    size="medium"
                    type="button"
                    onClick={async () => handleAddressLookup(address)}
                >
                    <PinIcon size="medium" />
                </Button>
            </div>

            {enableMap && (
                <MapComponent
                    center={center}
                    zoom={zoom}
                    markerLocation={markerLocation}
                    hasValidGeoCoordinates={hasValidGeoCoordinates}
                    errors={errors}
                    handleMapClick={handleMapClick}
                />
            )}
        </div>
    );
};

const getZoom = (sw: number, ne: number) => {
    const GLOBE_WIDTH = 256; // a constant in Google's map projection
    const MAP_WIDTH = 560;
    let angle = ne - sw;
    if (angle < 0) {
        angle += 360;
    }

    // Map pixel width
    return Math.round(Math.log2((MAP_WIDTH * 360) / angle / GLOBE_WIDTH));
};

interface MapComponentProps {
    center: google.maps.LatLngLiteral;
    zoom: number;
    markerLocation?: google.maps.LatLngLiteral;
    hasValidGeoCoordinates: boolean;
    errors: FormikErrors<Location | string>;
    handleMapClick: (...events: google.maps.MapMouseEvent[]) => void;
}

const MapComponent = ({
    center,
    zoom,
    markerLocation,
    hasValidGeoCoordinates,
    errors,
    handleMapClick,
}: MapComponentProps) => {
    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: config.google.apiKey,
    });

    if (!isLoaded) {
        return <Loader size="tiny" className="p-2" text="Loading Map" />;
    }

    return (
        <div className={clsx((errors as FormikErrors<Location>)?.geo && "rounded-lg border-2 border-danger")}>
            <GoogleMap
                mapContainerClassName="no-translate"
                mapContainerStyle={containerStyle}
                center={center}
                zoom={zoom}
                options={{ mapTypeControl: false, disableDefaultUI: true }}
                onClick={handleMapClick}
            >
                {markerLocation && hasValidGeoCoordinates && <Marker position={markerLocation} />}
            </GoogleMap>
        </div>
    );
};
